import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Link } from 'react-router-dom';
import MetaTags from 'react-meta-tags';
import SweetAlert from 'react-bootstrap-sweetalert';
import { Card, CardBody, Col, Container, Row, FormGroup, Label, Alert } from 'reactstrap';
import { AvField } from 'availity-reactstrap-validation';
import InputMask from 'react-input-mask';
import ToolkitProvider from 'react-bootstrap-table2-toolkit';
import { motion, AnimatePresence } from 'framer-motion';

import { marketingApi } from '../../services/MarketingService';

import Breadcrumbs from '../../components/Breadcrumb';
import PageLoader from '../../components/Loaders/newPageLoader';
import { showToast } from '../../components/Toast';
import useLoadingProgress from '../../hooks/useLoadingProgress';
import { MainContent } from '../../components/Table/MainContent';

export default function DomainsList({ profileData, loading }) {
  const {
    data = [],
    isLoading,
    isFetching,
    // error,
    refetch
  } = marketingApi.useFetchAllDomainsQuery(undefined, {
    refetchOnMountOrArgChange: false,
    refetchOnFocus: false
  });

  const [getCredentials] = marketingApi.useGetCredentialsMutation();
  const [addDomain] = marketingApi.useAddDomainMutation();
  const [updateDomain] = marketingApi.useUpdateDomainMutation();
  const [deleteDomain] = marketingApi.useDeleteDomainMutation();

  const [domains, setDomainsData] = useState([]);
  const [expiryDate, setExpiryDate] = useState('');
  const [correctDate, setCorrectDate] = useState(true);
  const [filteredDomains, setFilteredDomains] = useState([]);
  const [filters, setNoDbFilters ] = useState([]);
  const [error, setErrorMsg] = useState('');
  const [domain, setSelectedDomain] = useState([]);
  const [isEdit, setIsEdit] = useState(false);

  const { progress, step, visible } = useLoadingProgress({ isLoading, isFetching });
  useEffect(() => {
    if (data.domains) {
      setDomainsData(JSON.parse(JSON.stringify(data.domains)));
    }
  }, [data]);

  const [cardHeight, setCardHeight] = useState(0);
  const cardRef = useRef(null);
  useEffect(() => {
    if (!cardRef.current) return;
    const resizeObserver = new ResizeObserver((entries) => {
      for (let entry of entries) {
        const newHeight = entry.contentRect.height;
        if (newHeight > 0) setCardHeight(newHeight);
      }
    });
    resizeObserver.observe(cardRef.current);
    return () => resizeObserver.disconnect();
  }, []);

  useEffect(() => {
    const applyFilters = () => {
      let result = [...domains];

      if (filters?.providers?.length > 0) {
        result = result.filter(domain => filters?.providers.includes(domain.provider));
      }

      setFilteredDomains(result);
    };

    applyFilters();
  }, [filters, domains]);  

  // Toggle modal
  const [modalNewDomain, setModalNewDomain] = useState(false);
  /**
   * Toggles the visibility state of the "New Domain" modal.
   * Uses a callback to invert the current modal state.
   *
   * @function
   * @returns {void}
   */
  const toggleNewDomainModal = useCallback(() => {
    setModalNewDomain((prev) => !prev);
  }, []);
  /**
   * Handles the event when the "New Domain" button is clicked.
   * Resets the selected domain, disables edit mode, clears any error messages,
   * and opens the modal for creating a new domain.
   */
  const handleNewDomainClick = () => {
    setSelectedDomain([]);
    setIsEdit(false);
    setErrorMsg('');
    toggleNewDomainModal();
  };
  /**
   * Handles the edit action for a domain item.
   * Sets the selected domain details for editing, updates the edit mode state,
   * formats and sets the expiry date, and opens the domain modal.
   *
   */
  const handleDomainClickEdit = (domain) => {
    setSelectedDomain(domain);
    setIsEdit(true);
    setExpiryDate(new Date(domain.expiryDate).toLocaleDateString('en-GB'));
    toggleNewDomainModal();
  }
  /**
   * Checks if a given domain already exists in the domains list.
   *
   * Trims the domain string from the input object and compares it against
   * the trimmed domain strings of existing domain objects in the `domains` array.
   * Returns `true` if a match is found, otherwise `false`.
   *
   * @param {Object} newDomain - The domain object to check for existence.
   * @param {string} newDomain.domain - The domain string to check.
   * @returns {boolean} `true` if the domain exists in the list, otherwise `false`.
   */
  const checkExistingDomain = (newDomain) => {
    const newDom = newDomain.domain?.trim?.(); // Safe in case newDomain.service is null/undefined
    return domains.some((el) => {
      const existingDomain = el.domain?.trim?.();
      return existingDomain === newDom;
    });
  };
  /**
   * Handles the submission of the domain form for both adding and editing domains.
   * 
   * Depending on the `isEdit` flag, this function either updates an existing domain or adds a new one.
   * - When editing, it constructs an updated domain object and attempts to update it via `updateDomain`.
   * - When adding, it checks for duplicate domains using `checkExistingDomain` before attempting to add a new domain via `addDomain`.
   * 
   * Displays toast notifications for success or failure, and toggles the domain modal upon successful operations.
   * 
   * @async
   * @param {Event} e - The form submission event.
   * @param {Object} values - The form values containing domain details.
   */
  const handleDomainSubmit = async (e, values) => {
    if (isEdit) {
      const updDomain = {
        _id: domain._id,
        domain: values.domain,
        provider: values.provider,
        expiryDate: expiryDate.split('/').reverse().join('-'),
        loginUsed: values.loginUsed,
        passwordUsed: values.passwordUsed,
        pricePaid: values.pricePaid
      }
      try {
        console.log(updDomain)
        await updateDomain(updDomain).unwrap();
        toggleNewDomainModal();
        showToast('Domain is Edited');
      } catch (error) {
        console.log(error)
        showToast('Failed to Edit Domain', 'error');
      }
    } else {
      const newDomain = {
        domain: values["domain"],
        provider: values["provider"],
        expiryDate: expiryDate.split('/').reverse().join('-'),
        loginUsed: values["loginUsed"],
        passwordUsed: values["passwordUsed"],
        pricePaid: values["pricePaid"],
      }
      try {
        if (checkExistingDomain(newDomain) === true) {
          setErrorMsg("Domain already exists.");
        }else {
          await addDomain(newDomain).unwrap();
          toggleNewDomainModal();
          showToast('New Domain is Added');
        }
      } catch (error) {
        showToast('Failed to Add New Domain', 'error');
      }
    }
  }
  const [rowToDelete, setRowToDelete] = useState(null);
  const [showDeleteAlert, setShowDeleteAlert] = useState(false);
  const [showPasswordAlert, setShowPasswordAlert] = useState(false);
  const [showCredentialsAlert, setShowCredentialsAlert] = useState(false);
  /**
   * Handles changes to an input value, typically for a date field.
   * Checks if the month portion of the input (characters at positions 3 and 4) is valid (<= 12).
   * Updates the state to indicate whether the date is correct and sets the expiry date if valid.
   *
   * @param {Object} event - The event object from the input change.
   * @param {Object} event.target - The target element of the event.
   * @param {string} event.target.value - The current value of the input field.
   */
  const onChangeValue = (event) => {
    if (parseFloat(event.target.value.substring(3, 5)) > 12) {
      setCorrectDate(false);
    } else {
      setCorrectDate(true);
      setExpiryDate(event.target.value)
    }
  };

  /**
   * Handles the action of viewing credentials for a given domain.
   * Sets the selected domain and displays the password alert modal.
   *
   * @param {Object} domain - The domain object whose credentials are to be viewed.
   */
  const viewCredentials = (domain) => {
    setSelectedDomain(domain);
    setShowPasswordAlert(true)
  }
  /**
   * Handles the submission of user credentials for a selected domain.
   * 
   * This function constructs a credentials object using the provided password response,
   * the selected domain, and the user's profile data. It then attempts to retrieve
   * credentials from the server. On success, it updates the UI state to reflect the
   * successful operation and displays the credentials alert. On failure, it sets an
   * appropriate error message to be displayed to the user.
   *
   * @async
   * @param {string} response - The password or credential input provided by the user.
   * @returns {Promise<void>} Resolves when the credential handling process is complete.
   */
  const handleCredentialsSubmit = async (response) => {
    const creds = {
      domain: domain.domain,
      password: response,
      name: profileData.fname +' '+ profileData.sname,
      module: 'Bot Helper Notification',
      option: 'domainCredentials'
    }
    try {
      const res = await getCredentials(creds).unwrap(); // will throw on error (401, 500, etc.)
      console.log(res)
      setShowPasswordAlert(false); // hide alert if successful
      setErrorMsg(null); // clear old error
      setSelectedDomain(res)
      setShowCredentialsAlert(true)
      // do something with the result (res), maybe store domainInfo
    } catch (err) {
      // Handle rejected request
      const msg =
        err?.data || err?.error || 'Something went wrong. Please try again.';
      setErrorMsg(msg); // this will show inside <Alert>
    }
  }
  /**
   * Handles the delete action for a domain item.
   * Sets the selected domain, marks the row for deletion,
   * and displays the delete confirmation alert.
   *
   * @param {Object} domain - The domain object to be deleted.
   */
  const handleDomainClickDelete = (domain) => {
    setSelectedDomain(domain);
    setRowToDelete(domain._id);
    setShowDeleteAlert(true);
  };
  /**
   * Handles the deletion of a domain.
   * 
   * This asynchronous function attempts to delete the currently selected domain.
   * On success, it resets relevant UI states and displays a success toast notification.
   * On failure, it logs the error and displays an error toast notification.
   *
   * @async
   * @function
   * @returns {Promise<void>} Resolves when the deletion process is complete.
   */
  const handleDeleteDomain = async () => {
    try {
      await deleteDomain(rowToDelete);
      setRowToDelete(null);
      setShowDeleteAlert(false);
      setSelectedDomain([]);
      showToast('Domain Deleted Successfully');
    } catch (error) {
      showToast('Failed to delete row', 'error');
    }
  };
  /**
   * Renders the modal content for domain management, including form fields for service name, website, description,
   * payment option, price paid, expiry date, and comments. Utilizes AvField components for validation and InputMask
   * for date input formatting. Displays validation feedback for incorrect date formats.
   *
   * @constant
   * @type {JSX.Element}
   */
  const modalDomainContent = (
    <>
      <Row>
        <Col md={3}>
          <FormGroup className="mb-3">
            <AvField
              name="domain"
              label="Domain Name"
              type="text"
              errorMessage="Invalid name"
              validate={{
                required: {
                  value: true,
                },
              }}
              value={
                domain.domain || ""
              }
            />
          </FormGroup>
        </Col>
        <Col md={3}>
          <FormGroup className="mb-3">
            <AvField
              name="provider"
              label="Provider Name"
              type="text"
              errorMessage="Invalid provider"
              validate={{
                required: {
                  value: true,
                },
              }}
              value={
                domain.provider || ""
              }
            />
          </FormGroup>
        </Col>
        <Col md={3}>
          <FormGroup className='mb-3'>
            <Label>Date</Label>
            <InputMask
              mask='99/99/9999'
              value={expiryDate}
              name='expiryDate'
              onChange={onChangeValue}
              className={
                correctDate
                  ? ' form-control input-mask'
                  : 'form-control input-mask is-invalid'
              }
            />
            {!correctDate && (
              <div className='invalid-feedback'>
                Invalid Date. Please use dd/mm/yyyy format
              </div>
            )}
          </FormGroup>
        </Col>
        <Col md={3}>
          <FormGroup className='mb-3'>
            <AvField
              name='pricePaid'
              label='Price Paid'
              autoComplete='off'
              type='number'
              value={domain.pricePaid || 0}
            />
          </FormGroup>
        </Col>
      </Row>
      <Row>
        <Col md={6}>
          <FormGroup className="mb-3">
            <AvField
              name="loginUsed"
              label="Login Used"
              type="text"
              value={
                domain.loginUsed || ""
              }
            />
          </FormGroup>
        </Col>
        <Col md={6}>
          <FormGroup className="mb-3">
            <AvField
              name="passwordUsed"
              label="Password Used"
              type="password"
              value={
                domain.passwordUsed || ""
              }
            />
          </FormGroup>
        </Col>
      </Row>    
    </>
  );
  /**
   * Defines the column configuration for the domains table in the Marketing page.
   *
   * Each column object specifies how the data should be displayed, sorted, and formatted.
   * Includes custom formatters for rendering domain links, provider links, expiry date with color coding,
   * masked credentials, and action buttons for editing and deleting domains.
   *
   * @constant
   * @type {Array<Object>}
   * @property {string} text - The display name of the column header.
   * @property {string} dataField - The key in the data object to display in this column.
   * @property {boolean} [sort] - Whether the column is sortable.
   * @property {boolean} [editable] - Whether the column is editable.
   * @property {function} [formatter] - Custom function to format the cell content.
   * @property {boolean} [isDummyField] - Indicates if the column is a dummy field (not bound to data).
   */
  const columns = [
    {
      text: 'ID',
      dataField: '_id',
      sort: true,
      editable: false,
      formatter: (cell, row, rowIndex) => rowIndex + 1
    },
    {
      text: "Domain Name",
      dataField: "domain",
      formatter: (cellContent, domain) => (
        <>
          <h5 className="font-size-16 mb-1">
            <a href={"https://"+domain.domain} target='_blank' rel="noreferrer">
              {domain.domain}
            </a>
          </h5>
        </>
      ),
    },
    {
      text: "Provider",
      dataField: "provider",
      sort: true,
      formatter: (cellContent, domain) => (
        <>
          <h5 className="font-size-16 mb-1">
            <a href={"https://"+domain.provider} target="_blank" rel="noreferrer">
              {domain.provider}
            </a>
          </h5>
        </>
      ),
    },
    {
      text: "Expiry Date",
      dataField: "expiryDate",
      sort: true,
      formatter: (cellContent, domain) => (
        <h5
          className={`font-size-16 mb-1 ${
            domain.expiryDate
              ? (() => {
                  const expiry = new Date(domain.expiryDate);
                  const today = new Date();
                  const diffTime = expiry.getTime() - today.getTime();
                  const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

                  if (diffDays < 30) return 'text-danger'; // red text
                  if (diffDays < 60) return 'text-warning'; // yellow/orange text
                  return '';
                })()
              : ''
          }`}
        >
          {domain.expiryDate !== null &&
            new Date(domain.expiryDate).toLocaleDateString('en-GB')}
        </h5>
      ),
    },
    {
      text: "Credentials",
      dataField: "login",
      sort: true,
      formatter: (cellContent, domain, index) => (
        <>
          <h5 className="font-size-16 mb-1">
            <b>Login:</b> {( domain.loginUsed.replace(/./g, '*').substring(0, 5) )}
          </h5>
          <h5 className="font-size-16 mb-1">
            <b>Password:</b>
            <span className="password-container ms-2">
              {( domain.passwordUsed.replace(/./g, '*').substring(0, 8) )}
              <Link className="text-primary ms-2" to="#">
                <i
                  className="mdi mdi-eye font-size-18"
                  id="edittooltip"
                  onClick={() => viewCredentials(domain)}
                ></i>
              </Link>
            </span>
          </h5>
        </>
      )
    },
    {
      dataField: "menu",
      isDummyField: true,
      editable: false,
      text: "Action",
      formatter: (cellContent, domain) => (
        <div className="d-flex gap-3">
          {profileData.accType === 'Admin' && 
            <>
              <Link className="text-success" to="#">
                <i
                  className="mdi mdi-pencil font-size-18"
                  id="edittooltip"
                  onClick={() => handleDomainClickEdit(domain)}
                ></i>
              </Link>
              <Link className="text-danger" to="#">
                <i
                  className="mdi mdi-delete font-size-18"
                  id="deletetooltip"
                  onClick={() => handleDomainClickDelete(domain)}
                ></i>
              </Link>
            </>
          }
        </div>
      ),
    },
  ];
  /**
   * An array specifying the default sorting configuration for a table.
   * Each object in the array defines the column to sort by (`dataField`)
   * and the sort order (`order`), which can be either 'asc' for ascending
   * or 'desc' for descending. If the specified `dataField` does not match
   * any defined column, it will be ignored.
   *
   * @type {Array<{dataField: string, order: 'asc' | 'desc'}>}
   */
  const defaultSorted = [
    {
      dataField: 'expiryDate', // if dataField is not match to any column you defined, it will be ignored.
      order: 'asc' // desc or asc
    }
  ];
  const providers = [...new Set(domains.map(u => u.provider))].map(type => ({ key: type, label: type, icon: '', colorClass: 'bg-primary' }));
  return (
    <React.Fragment>
      <PageLoader progress={progress} step={step} visible={visible} />
      <div className='page-content'>
        <MetaTags>
          <title>Domains List </title>
        </MetaTags>
        <Container fluid>
          {profileData.permissions.includes('domainsAccess') ? (
            <>
              <Breadcrumbs title='Marketing' breadcrumbItem='Domains List' />
              <Row className='card-section'>
                <Col lg='12'>
                  <div className='pcss3t logtb pcss3t-effect-scale pcss3t-theme-1'>
                    <ul>
                      <AnimatePresence key='1' exitBeforeEnter>
                        <motion.li
                          className={`tab-content tab-content-1 typography`}
                          initial={{ opacity: 0, x: 30 }}
                          animate={{ opacity: 1, x: 0 }}
                          exit={{ opacity: 0, x: -30 }}
                          transition={{ duration: 0.3 }}
                          style={{ display: 'block' }}
                        >
                          <div id='card-section' ref={cardRef}>
                            <Row className='card-section'>
                              <Col lg='12'>
                                <Card>
                                  <CardBody className='cardBodySticky'>
                                    <ToolkitProvider
                                      keyField='_id'
                                      columns={columns}
                                      data={filteredDomains.length > 0 ? filteredDomains : domains}
                                      search
                                    >
                                      {(props) => (
                                        <React.Fragment>
                                          <MainContent
                                            page={{ 
                                              pg: 'domains', 
                                              props: props,
                                              cardHeight, 
                                              showSearch: true, 
                                              showCSV: true, 
                                              showInTblFltr: true, 
                                              showDbFilter: false, 
                                              showBtn: true, 
                                              columns: props.baseProps.columns,
                                              data: props.baseProps.data,
                                              defaultSorted,
                                            }}
                                            notificationSettings={{ showNotification: true, keyword: 'domain', module: 'Bot Helper Notification' }}
                                            // filter options
                                            filterOptions={{ providers: providers }}
                                            setNoDbFilters={setNoDbFilters}
                                            // options for adding/updating modal and button
                                            btnCrtModalOptions={{ 
                                              btnLabelCrt: 'ADD DOMAIN',
                                              modalSize:'lg',
                                              modalTitle: isEdit ? 'Edit Domain' : 'Add Domain',
                                              modalBtnLabel: isEdit ? 'Edit Domain' : 'Add Domain',
                                              modalAdd: modalNewDomain,
                                              toggleAddModal: toggleNewDomainModal,
                                              handleNewClick: handleNewDomainClick,
                                              modalContent: modalDomainContent,
                                              handleSubmit: handleDomainSubmit,
                                              errorMsg: error
                                            }}
                                            // table data opotions
                                            tableOptions={{ 
                                              includeSelect: false, 
                                              // calculateFooter: true, 
                                              manualColumnWidthsIndex: [5], 
                                              manualColumnWidths: [70] 
                                            }}
                                          />
                                          <SweetAlert
                                            show={showDeleteAlert}
                                            danger
                                            showCancel
                                            confirmBtnText='Yes, delete it!'
                                            confirmBtnBsStyle='danger'
                                            title='Are you sure?'
                                            onConfirm={handleDeleteDomain}
                                            onCancel={() => setShowDeleteAlert(false)}
                                            focusCancelBtn
                                          >
                                            <p className='fw-bold'>{domain.domain}</p>{' '}
                                            will be deleted from the system!
                                          </SweetAlert>
                                          <SweetAlert
                                            show={showPasswordAlert}
                                            input
                                            showCancel
                                            inputType="password"
                                            cancelBtnBsStyle="light"
                                            title="Password:"
                                            onConfirm={(response) => handleCredentialsSubmit(response)}
                                            onCancel={() => {
                                              setShowPasswordAlert(false);
                                              setErrorMsg(null); // clear error when cancelled
                                            }}
                                          >
                                            {error ? <Alert color="danger">{error}</Alert> : null}
                                          </SweetAlert>
                                          <SweetAlert
                                            show={showCredentialsAlert}
                                            info
                                            showCancel
                                            confirmBtnText='Ok'
                                            confirmBtnBsStyle='info'
                                            title='Information'
                                            onConfirm={() => setShowCredentialsAlert(false)}
                                            onCancel={() => setShowCredentialsAlert(false)}
                                            focusCancelBtn
                                          >
                                            Login: <p className="fw-bold">{domain.loginUsed}</p>
                                            Password: <p className="fw-bold">{domain.passwordUsed}</p>
                                          </SweetAlert>
                                        </React.Fragment>
                                      )}
                                    </ToolkitProvider>
                                  </CardBody>
                                </Card>
                              </Col>
                            </Row>
                          </div>
                        </motion.li>
                      </AnimatePresence>
                    </ul>
                  </div>
                </Col>
              </Row>
            </>
          ) : (
            <Alert color="danger">{" "} You do not have access to this page.{" "}</Alert>
          )}
        </Container>
      </div>
    </React.Fragment>
  );
}